const motionInstances = new Set();
let currentMotion;

class GoldenMotion extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.video = this.querySelector('video');
    this.button = this.querySelector('button');
    if (!this.video || !this.button) return;
    motionInstances.add(this);
    this.preference = matchMedia('(prefers-reduced-motion: reduce)');
    this.connection = navigator.connection;
    this.userPaused = false;
    this.ratio = 0;
    this.video.muted = true;
    this.video.defaultMuted = true;
    this.onToggle = () => {
      if (!this.video.paused) {
        this.userPaused = true;
        this.video.pause();
      } else {
        this.userPaused = false;
        this.start(true);
      }
    };
    this.onPlaying = () => {
      if (currentMotion !== this || document.hidden) { this.video.pause(); return; }
      this.classList.add('is-playing');
      this.update();
    };
    this.onPause = () => this.update();
    this.onFailure = () => { this.classList.remove('is-playing'); this.video.pause(); this.update(); };
    this.onPreference = () => {
      if (this.constrained()) { this.video.pause(); this.classList.remove('is-playing'); }
      else this.coordinate();
    };
    this.onVisibility = () => { if (document.hidden) this.video.pause(); else this.coordinate(); };
    this.button.addEventListener('click', this.onToggle);
    this.video.addEventListener('playing', this.onPlaying);
    this.video.addEventListener('pause', this.onPause);
    this.video.addEventListener('error', this.onFailure);
    this.preference.addEventListener('change', this.onPreference);
    this.connection?.addEventListener?.('change', this.onPreference);
    document.addEventListener('visibilitychange', this.onVisibility);
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(entries => {
        this.ratio = entries[0].intersectionRatio;
        if (this.ratio < .4) this.video.pause();
        this.coordinate();
      }, { threshold: [0, .4, .65, 1] });
      this.observer.observe(this);
    }
    this.update();
  }
  constrained() {
    return this.preference.matches || this.connection?.saveData || ['slow-2g', '2g'].includes(this.connection?.effectiveType) || (this.connection?.downlink > 0 && this.connection.downlink < 1);
  }
  coordinate() {
    const candidates = [...motionInstances].filter(item => item.ratio >= .4 && !item.userPaused && !item.constrained());
    candidates.sort((a, b) => b.ratio - a.ratio);
    const next = candidates[0];
    if (document.hidden || !next) return;
    if (currentMotion && currentMotion !== next) currentMotion.video.pause();
    next.start();
  }
  async start(manual = false) {
    if (document.hidden || (!manual && (this.ratio < .4 || this.userPaused || this.constrained()))) return;
    for (const item of motionInstances) if (item !== this) item.video.pause();
    currentMotion = this;
    if (!this.loaded) {
      const sources = [...this.video.querySelectorAll('source')];
      const source = sources.find(item => item.media && matchMedia(item.media).matches) || sources.find(item => !item.media);
      if (!source?.dataset.src) return;
      for (const item of sources) {
        if (item === source) item.src = item.dataset.src;
        else item.removeAttribute('src');
      }
      this.loaded = true;
      this.video.load();
    }
    try { await this.video.play(); } catch { this.classList.remove('is-playing'); this.update(); }
  }
  update() {
    const label = this.video.paused ? this.dataset.play : this.dataset.pause;
    this.button.textContent = label;
    this.button.setAttribute('aria-label', label);
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.video?.pause();
    this.button?.removeEventListener('click', this.onToggle);
    this.video?.removeEventListener('playing', this.onPlaying);
    this.video?.removeEventListener('pause', this.onPause);
    this.video?.removeEventListener('error', this.onFailure);
    this.preference?.removeEventListener('change', this.onPreference);
    this.connection?.removeEventListener?.('change', this.onPreference);
    document.removeEventListener('visibilitychange', this.onVisibility);
    motionInstances.delete(this);
    if (currentMotion === this) currentMotion = undefined;
    this.initialized = false;
  }
}
if (!customElements.get('gh-motion')) customElements.define('gh-motion', GoldenMotion);

document.addEventListener('keydown', event => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.gh-header details[open]').forEach(item => {
    item.open = false;
    item.querySelector('summary')?.focus();
  });
});
document.addEventListener('click', event => {
  const bag = event.target.closest?.('a.gh-bag');
  const drawer = document.getElementById('cart-drawer');
  if (bag && drawer && typeof drawer.open === 'function' && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
    event.preventDefault();
    drawer.open();
  }
  document.querySelectorAll('.gh-header details[open]').forEach(item => {
    if (!item.contains(event.target)) item.open = false;
  });
});

const headerGroup = document.getElementById('header-group');
if (headerGroup) {
  const measure = () => {
    const height = Math.max(0, headerGroup.getBoundingClientRect().height - 28);
    document.body.style.setProperty('--header-height', height + 'px');
    document.body.style.setProperty('--header-group-height', height + 'px');
  };
  new ResizeObserver(measure).observe(headerGroup);
  measure();
}

const trustSlot = document.querySelector('[data-gh-trust-slot]');
if (trustSlot) {
  const moveBadge = () => {
    const badge = document.getElementById('trustedsite-tm-image');
    if (badge && badge.parentElement !== trustSlot) trustSlot.append(badge);
  };
  const badgeObserver = new MutationObserver(moveBadge);
  badgeObserver.observe(document.body, { childList: true, subtree: true });
  moveBadge();
}

const scrollStories = [...document.querySelectorAll('gh-scroll-story')];
const motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
const pageScroller = document.querySelector('.page-wrapper');
let scheduled = false;
const updateStories = () => {
  scheduled = false;
  for (const story of scrollStories) {
    const image = story.querySelector('img');
    if (!image) continue;
    if (motionPreference.matches || innerWidth < 990) { image.style.transform = ''; continue; }
    const rect = story.getBoundingClientRect();
    const progress = Math.max(0, Math.min(1, -rect.top / Math.max(1, rect.height - innerHeight)));
    image.style.transform = 'scale(' + (1.04 + progress * .035) + ') translateY(' + (1 - progress) * 1.2 + '%)';
  }
};
const queueStoryUpdate = () => { if (!scheduled) { scheduled = true; requestAnimationFrame(updateStories); } };
if (scrollStories.length) {
  pageScroller?.addEventListener('scroll', queueStoryUpdate, { passive: true });
  window.addEventListener('scroll', queueStoryUpdate, { passive: true });
  window.addEventListener('resize', queueStoryUpdate, { passive: true });
  motionPreference.addEventListener('change', queueStoryUpdate);
  updateStories();
}

if (new URLSearchParams(location.search).has('gh_audit') && 'PerformanceObserver' in window) {
  const root = document.documentElement;
  const observe = (type, callback, extra = {}) => {
    try { new PerformanceObserver(callback).observe({ type, buffered: true, ...extra }); } catch {}
  };
  observe('largest-contentful-paint', list => {
    const entry = list.getEntries().at(-1);
    if (entry) root.dataset.ghLcpMs = Math.round(entry.startTime);
  });
  let start = 0;
  let previous = 0;
  let session = 0;
  let maximum = 0;
  root.dataset.ghCls = '0';
  observe('layout-shift', list => {
    for (const entry of list.getEntries()) {
      if (entry.hadRecentInput) continue;
      if (entry.startTime - previous > 1000 || entry.startTime - start > 5000) {
        start = entry.startTime;
        session = 0;
      }
      previous = entry.startTime;
      session += entry.value;
      maximum = Math.max(maximum, session);
    }
    root.dataset.ghCls = maximum.toFixed(5);
  });
  let longestInteraction = 0;
  observe('event', list => {
    for (const entry of list.getEntries()) {
      if (entry.interactionId) longestInteraction = Math.max(longestInteraction, entry.duration);
    }
    root.dataset.ghInteractionMaxMs = String(longestInteraction);
  }, { durationThreshold: 16 });
}
